import type { Tendencies as TendencyData } from '../lib/api';

/**
 * How often a coach reaches for each option, against how often it was the best one.
 *
 * A grade says one call was wrong. This says whether the same call is wrong every week,
 * which is the difference between a mistake and a habit. Two bars per option: what he
 * did, and what the situation asked for.
 */

const LABEL: Record<string, string> = {
  go: 'Go for it', punt: 'Punt', field_goal: 'Field goal',
  go_for_two: 'Go for two', kick_xp: 'Kick the extra point',
  run: 'Run', pass: 'Pass', deep: 'Throw deep', short: 'Throw short',
  onside: 'Onside kick', deep_kick: 'Kick deep', squib_or_pooch: 'Squib',
  return: 'Return it', touchback: 'Take the touchback', fair_catch: 'Fair catch',
};

const humanize = (s: string) => LABEL[s] ?? s.replace(/_/g, ' ');

export function TendencyChart({ data }: { data: TendencyData }) {
  // biggest habit first, in either direction
  const options = Object.entries(data.options).sort((a, b) => Math.abs(b[1].bias) - Math.abs(a[1].bias));
  const strongest = options[0];

  return (
    <section>
      <p className="kicker mb-1">Habits</p>
      <h3 className="h3">Habit, or one bad call?</h3>
      <p className="body-muted mt-1 max-w-[56ch] text-[14px]">
        Across {data.decisions.toLocaleString()} {humanize(data.category).toLowerCase()} calls where an
        alternative had enough history to compare against. The lighter bar is how often that
        option was the best one available.
      </p>

      {strongest && Math.abs(strongest[1].bias) >= 0.08 && (
        <p className="mt-3 border-l-2 border-blue bg-blueTint px-3 py-2 text-[14px]">
          He {strongest[1].bias > 0 ? 'reaches for' : 'avoids'}{' '}
          <span className="font-semibold">{humanize(strongest[0]).toLowerCase()}</span>{' '}
          {Math.abs(Math.round(strongest[1].bias * 100))} points{' '}
          {strongest[1].bias > 0 ? 'more' : 'less'} often than the situations call for.
        </p>
      )}

      <dl className="mt-4 flex flex-col gap-4">
        {options.map(([option, v]) => (
          <div key={option}>
            <div className="flex items-baseline justify-between gap-3">
              <dt className="font-display text-[16px] font-bold">{humanize(option)}</dt>
              <dd className="num text-[13px] text-muted">
                chose {Math.round(v.chose_rate * 100)}%
                <span className="mx-1.5 text-faint">/</span>
                best {Math.round(v.optimal_rate * 100)}%
                <span className={`ml-2 font-semibold ${v.bias > 0.02 ? 'text-red' : v.bias < -0.02 ? 'text-amber' : 'text-muted'}`}>
                  {v.bias > 0 ? '+' : ''}{Math.round(v.bias * 100)}
                </span>
              </dd>
            </div>
            <div className="mt-1.5 flex flex-col gap-1">
              <div className="h-2.5 w-full overflow-hidden rounded-sm bg-wash" title={`Chose it ${v.chose} times`}>
                <div className="h-full bg-blue" style={{ width: `${Math.min(100, v.chose_rate * 100)}%` }} />
              </div>
              <div className="h-2.5 w-full overflow-hidden rounded-sm bg-wash" title={`It was the best option ${v.was_optimal} times`}>
                <div className="h-full bg-blue/35" style={{ width: `${Math.min(100, v.optimal_rate * 100)}%` }} />
              </div>
            </div>
          </div>
        ))}
      </dl>
    </section>
  );
}
